/**
 * WAV Encoder: Float32 PCM to WAV format
 * Target: 48kHz, 16-bit, mono/stereo
 */

export interface WAVEncoderOptions {
  sampleRate?: number;
  channels?: number;
  bitDepth?: 16 | 24;
}

const DEFAULT_SAMPLE_RATE = 48000;
const DEFAULT_CHANNELS = 1;
const DEFAULT_BIT_DEPTH = 16;

class WAVEncoder {
  private sampleRate: number;
  private channels: number;
  private bitDepth: 16 | 24;

  constructor(options: WAVEncoderOptions = {}) {
    this.sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
    this.channels = options.channels ?? DEFAULT_CHANNELS;
    this.bitDepth = options.bitDepth ?? DEFAULT_BIT_DEPTH;
  }

  /**
   * Encode Float32 PCM audio to WAV blob
   */
  public encode(pcmFloat32: Float32Array): Blob {
    const bytesPerSample = this.bitDepth / 8;
    const dataSize = pcmFloat32.length * bytesPerSample;
    const headerSize = 44; // WAV header size
    const fileSize = headerSize + dataSize - 8;

    const buffer = new ArrayBuffer(headerSize + dataSize);
    const view = new DataView(buffer);

    // WAV header (big-endian / little-endian per spec)
    this.writeString(view, 0, "RIFF");
    view.setUint32(4, fileSize, true); // file size - 8
    this.writeString(view, 8, "WAVE");
    this.writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true); // subchunk size
    view.setUint16(20, 1, true); // audio format (1 = PCM)
    view.setUint16(22, this.channels, true);
    view.setUint32(24, this.sampleRate, true);
    view.setUint32(28, this.sampleRate * this.channels * bytesPerSample, true); // byte rate
    view.setUint16(32, this.channels * bytesPerSample, true); // block align
    view.setUint16(34, this.bitDepth, true); // bits per sample
    this.writeString(view, 36, "data");
    view.setUint32(40, dataSize, true);

    // Write PCM data
    this.writePCM(view, 44, pcmFloat32);

    return new Blob([buffer], { type: "audio/wav" });
  }

  private writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  private writePCM(
    view: DataView,
    offset: number,
    pcmFloat32: Float32Array
  ): void {
    const bytesPerSample = this.bitDepth / 8;

    if (this.bitDepth === 16) {
      for (let i = 0; i < pcmFloat32.length; i++) {
        const sample = Math.max(-1, Math.min(1, pcmFloat32[i]));
        const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(offset + i * 2, int16, true);
      }
    } else if (this.bitDepth === 24) {
      for (let i = 0; i < pcmFloat32.length; i++) {
        const sample = Math.max(-1, Math.min(1, pcmFloat32[i]));
        const int24 = sample < 0 ? sample * 0x800000 : sample * 0x7fffff;
        const int24Int = Math.trunc(int24);

        // 24-bit little-endian write
        view.setUint8(offset + i * 3, int24Int & 0xff);
        view.setUint8(offset + i * 3 + 1, (int24Int >> 8) & 0xff);
        view.setUint8(offset + i * 3 + 2, (int24Int >> 16) & 0xff);
      }
    }
  }

  /**
   * Resample Float32 data from one sample rate to another
   */
  public static resample(
    pcmFloat32: Float32Array,
    fromSampleRate: number,
    toSampleRate: number
  ): Float32Array {
    if (fromSampleRate === toSampleRate) {
      return pcmFloat32;
    }

    const ratio = toSampleRate / fromSampleRate;
    const newLength = Math.round(pcmFloat32.length * ratio);
    const resampled = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const sourceIndex = i / ratio;
      const indexFloor = Math.floor(sourceIndex);
      const indexCeil = Math.ceil(sourceIndex);
      const fraction = sourceIndex - indexFloor;

      if (indexCeil >= pcmFloat32.length) {
        resampled[i] = pcmFloat32[indexFloor];
      } else {
        resampled[i] =
          pcmFloat32[indexFloor] * (1 - fraction) +
          pcmFloat32[indexCeil] * fraction;
      }
    }

    return resampled;
  }
}

export default WAVEncoder;
